import axios, { AxiosRequestConfig } from 'axios';
import { ZodSchema, z } from 'zod';
import { fromZodError } from 'zod-validation-error';
import * as Sentry from '@sentry/vue';
import router from '../router/index';
import type { HTTPMethod } from './types';
import { ResponseError } from './responseError';

type FetcherConfig<Schema extends ZodSchema<any, any> | null> = {
  readonly method: HTTPMethod;
  readonly schema?: Schema;
  readonly body?: object;
  readonly config?: AxiosRequestConfig;
  readonly customApiUrl?: string;
};
const API_URL = process.env.VUE_APP_API_URL;

export async function fetcher<Schema extends ZodSchema<any, any> | null>(
  path: string,
  { method, body, config, schema, customApiUrl }: FetcherConfig<Schema>
): Promise<Schema extends ZodSchema<any, any> ? any : null> {
  try {
    axios.defaults.withCredentials = true;
    axios.defaults.withXSRFToken = true;

    if (!API_URL) {
      throw new ResponseError('API_URL environment variable is not set', 111);
    }

    const makeRequest = async () =>
      axios.request({
        url: `${customApiUrl || API_URL}${path}`,
        method,
        headers: {
          'Content-Type': 'application/json',
          'Allow-Access-Control-Origin': '*',
          Accept: 'application/json',
          ...config?.headers,
        },
        data: body ? JSON.stringify(body) : undefined,
        ...config,
      });

    const response = await makeRequest();
    const data = response.data.data;

    if (path === '/csrf-cookie') {
      return {} as any;
    }

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(fromZodError(parsedData.error));
        console.error(path);

        return data as z.infer<typeof schema>;
      }

      return parsedData.data;
    }

    return data;
  } catch (err: any) {
    console.error(err);
    console.error(path);

    if (axios.isAxiosError(err)) {
      const status = err.response?.status;

      if (status === 401) {
        router.push('/login');
        return null as any;
      }

      if (status === 403) {
        router.push({
          name: 'ErrorView',
          params: {
            status: 403,
          },
        });
        return null as any;
      }

      if (status === 419) {
        try {
          await axios.get(`${customApiUrl || API_URL}/csrf-cookie`);
          const retryResponse = await axios.request({
            url: `${API_URL}${path}`,
            method,
            headers: {
              'Content-Type': 'application/json',
              'Allow-Access-Control-Origin': '*',
              Accept: 'application/json',
              ...config?.headers,
            },
            data: body ? JSON.stringify(body) : undefined,
            ...config,
          });

          const retryData = retryResponse.data.data;

          if (schema) {
            const parsedData = schema.safeParse(retryData);

            if (!parsedData.success) {
              console.error(fromZodError(parsedData.error));
              console.error(path);

              return retryData as z.infer<typeof schema>;
            }

            return parsedData.data;
          }

          return retryData;
        } catch (retryError) {
          Sentry.captureException(retryError);
          router.push('/login');
          return null as any;
        }
      }

      if (status === 422) {
        throw new ResponseError(err.message, 422);
      } else if (method !== 'GET') {
        Sentry.captureException(err);
        throw err;
      }
    }

    if (err.message === 'Network Error' || err.message === 'Failed to fetch') {
      Sentry.captureException(err);
      router.push({
        name: 'ErrorView',
        params: {
          status: 400,
        },
      });
      return null as any;
    }

    Sentry.captureException(err);
    router.push({
      name: 'ErrorView',
      params: {
        status: 400,
      },
    });
    return null as any;
  }
}
